/**
 * Wolf (Bedrock numbers): wild 8 HP / 3 bite, tamed 20 HP / 4 bite.
 *
 * Taming: right-click a wild wolf holding a bone, 1/3 chance each (the bone is
 * used up either way). A tamed wolf starts sitting with a red collar; right-click
 * with a non-food item (or empty hand) toggles sit / stand. Standing, it follows
 * its owner (running when far), teleports to them past 12 blocks, and attacks the
 * mob the owner last hit or the mob that last hurt the owner. Meat heals it.
 * Wild wolves are neutral: hit one and it (plus wild wolves within 16 blocks)
 * turns on the player for 30 s.
 *
 * Renderer state is packed into `view.color`: bits 0-3 collar dye (0-15),
 * bit 4 tamed, bit 5 sitting.
 *
 * Not done: hunting sheep / rabbits / skeletons, wolf breeding, armour, dye.
 */

import { I } from '../../game/itemIds';
import { IS_SOLID } from '../../blocks';
import type { Stack } from '../../game/types';
import { Mob, type MobCtx } from '../mob';

/** Wiring the EntityManager installs (target selection and mob-vs-mob damage). */
export const wolfHooks: {
  /** The mob a tame wolf should fight for its owner right now, or null. */
  ownerTarget?: () => Mob | null;
  /** Damage `victim` as `attacker` (knockback, drops, no player credit). */
  attack?: (attacker: Mob, victim: Mob, dmg: number) => void;
} = {};

export const WILD_HEALTH = 8;
export const TAME_HEALTH = 20;
export const TAME_CHANCE = 1 / 3;
export const TELEPORT_DIST = 12;
/** [item, hearts healed x2 = HP] */
export const WOLF_FOOD: ReadonlyMap<number, number> = new Map([
  [I.BEEF, 3], [I.COOKED_BEEF, 8], [I.PORKCHOP, 3], [I.COOKED_PORKCHOP, 8], [I.MUTTON, 2], [I.COOKED_MUTTON, 6],
  [I.CHICKEN, 2], [I.COOKED_CHICKEN, 6], [I.ROTTEN_FLESH, 4],
]);

const wildWolves = new Set<Wolf>();

export class Wolf extends Mob {
  tame = false;
  sitting = false;
  /** Collar dye index (0-15, vanilla default red = 14). */
  collar = 14;
  /** Ticks of anger at the player left (wild wolves). */
  anger = 0;
  private attackCd = 0;
  private tpCd = 0;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'wolf', x, y, z, { width: 0.6, height: 0.85, eye: 0.68, health: WILD_HEALTH, speed: 0.3 }, rnd);
    wildWolves.add(this);
  }

  private setMaxHealth(h: number): void { (this as unknown as { maxHealth: number }).maxHealth = h; }

  /** Offer a bone: returns true when the item was used up (tamed or not). */
  tryTame(rnd: () => number): boolean {
    if (this.tame || !this.alive) return false;
    if (rnd() < TAME_CHANCE) {
      this.tame = true;
      this.sitting = true;
      this.anger = 0;
      this.setMaxHealth(TAME_HEALTH);
      this.health = TAME_HEALTH;
      this.host?.effect('heart', this.x, this.y + this.height + 0.3, this.z);
    } else {
      this.host?.effect('poof', this.x, this.y + this.height * 0.6, this.z);
    }
    return true;
  }

  /**
   * Right-click with `held` (id 0 = empty hand). Returns whether the click was
   * handled and whether an item was consumed.
   */
  interact(held: number, rnd: () => number): { handled: boolean; consumed: boolean } {
    if (!this.alive) return { handled: false, consumed: false };
    if (!this.tame) {
      if (held === I.BONE) return { handled: true, consumed: this.tryTame(rnd) };
      return { handled: false, consumed: false };
    }
    const heal = WOLF_FOOD.get(held);
    if (heal !== undefined && this.health < this.maxHealth) {
      this.health = Math.min(this.maxHealth, this.health + heal);
      this.host?.effect('heart', this.x, this.y + this.height + 0.3, this.z);
      return { handled: true, consumed: true };
    }
    if (heal !== undefined) return { handled: false, consumed: false };
    this.sitting = !this.sitting;
    return { handled: true, consumed: false };
  }

  override onHurtBy(): void {
    if (this.tame) return;
    // pack anger: this wolf and every wild wolf within 16 blocks
    for (const w of wildWolves) {
      if (w.removed) { wildWolves.delete(w); continue; }
      if (w.tame || w.dying || w.realm !== this.realm) continue;
      const dx = w.x - this.x, dy = w.y - this.y, dz = w.z - this.z;
      if (w === this || dx * dx + dy * dy + dz * dz <= 256) { w.anger = 600; w.retaliate = 100; }
    }
  }

  override hurt(amount: number): boolean {
    const r = super.hurt(amount);
    if (r && this.tame) this.sitting = false; // being hit stands it up
    return r;
  }

  xpValue(): number { return 0; }
  loot(): Stack[] { return []; }

  saveExtra(): unknown { return [this.tame ? 1 : 0, this.sitting ? 1 : 0, this.collar, this.anger]; }
  loadExtra(d: unknown): void {
    const a = d as number[];
    this.tame = a?.[0] === 1;
    this.sitting = a?.[1] === 1;
    this.collar = typeof a?.[2] === 'number' ? a[2] : 14;
    this.anger = typeof a?.[3] === 'number' ? a[3] : 0;
    if (this.tame) this.setMaxHealth(TAME_HEALTH);
  }

  /** Move next to the owner (vanilla FollowOwnerGoal teleportToOwner). */
  private teleportToOwner(ctx: MobCtx): boolean {
    const p = ctx.host.player, w = this.w, rnd = ctx.host.rnd;
    for (let i = 0; i < 12; i++) {
      const tx = Math.floor(p.x + (rnd() * 6 - 3)), tz = Math.floor(p.z + (rnd() * 6 - 3));
      const ty = Math.floor(p.y) + Math.floor(rnd() * 3) - 1;
      if (Math.abs(tx + 0.5 - p.x) < 2 && Math.abs(tz + 0.5 - p.z) < 2) continue;
      if (IS_SOLID[w.getBlock(tx, ty - 1, tz)] === 1 && !IS_SOLID[w.getBlock(tx, ty, tz)] && !IS_SOLID[w.getBlock(tx, ty + 1, tz)]) {
        this.x = this.px = tx + 0.5; this.y = this.py = ty; this.z = this.pz = tz + 0.5;
        this.vx = this.vy = this.vz = 0;
        return true;
      }
    }
    return false;
  }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.tpCd > 0) this.tpCd--;
    if (this.anger > 0) this.anger--;
    const p = ctx.host.player;
    if (this.tame) { this.tameAi(ctx); return; }

    // wild: neutral unless angered (hit by the player)
    if (this.anger > 0 && ctx.targetable()) {
      this.aggressive = true;
      this.faceToward(p.x, p.z); this.lookAt(p.x, p.eyeY, p.z);
      this.steerTo(ctx, p.x, p.y, p.z, 1.4, 6);
      if (this.attackCd === 0 && this.inMeleeReach(ctx)) {
        this.attackCd = 20; this.swing();
        ctx.hurtPlayer(this, 3, 0.4);
      }
      return;
    }
    this.stroll(ctx, 0.8, 4, true);
    this.lookAtNearPlayer(ctx);
  }

  private tameAi(ctx: MobCtx): void {
    const p = ctx.host.player;
    if (this.sitting) { this.lookAtNearPlayer(ctx); return; }
    // fight for the owner
    const t = wolfHooks.ownerTarget?.() ?? null;
    if (t && t !== this && t.alive && !(t instanceof Wolf && t.tame)) {
      const dx = t.x - this.x, dz = t.z - this.z;
      const d = Math.hypot(dx, dz);
      if (d < 20) {
        this.aggressive = true;
        this.faceToward(t.x, t.z); this.lookAt(t.x, t.y + t.height * 0.5, t.z);
        this.steerTo(ctx, t.x, t.y, t.z, 1.6, 6);
        const reach = this.width / 2 + t.width / 2 + 0.9;
        if (this.attackCd === 0 && d < reach && Math.abs(t.y - this.y) < 1.6) {
          this.attackCd = 20; this.swing();
          wolfHooks.attack?.(this, t, 4);
        }
        return;
      }
    }
    // follow the owner
    const dx = p.x - this.x, dz = p.z - this.z;
    const d2 = dx * dx + dz * dz;
    if (ctx.targetable() || p.alive) {
      if (d2 > TELEPORT_DIST * TELEPORT_DIST && this.tpCd === 0) {
        this.tpCd = 20;
        if (this.teleportToOwner(ctx)) return;
      }
      if (d2 > 4 * 4) {
        this.faceToward(p.x, p.z);
        this.steerTo(ctx, p.x, p.y, p.z, d2 > 8 * 8 ? 2 : 1.4, 6);
        this.lookAt(p.x, p.eyeY, p.z);
        return;
      }
    }
    if (d2 < 36) { this.lookAt(p.x, p.eyeY, p.z); return; }
    this.stroll(ctx, 0.6, 4, true);
  }

  override syncView(host: Parameters<Mob['syncView']>[0]): void {
    super.syncView(host);
    this.view.color = (this.collar & 15) | (this.tame ? 16 : 0) | (this.sitting ? 32 : 0);
  }
}

export const isTamedWolf = (m: Mob): boolean => m instanceof Wolf && m.tame;
